// src/api/userManagementService.ts
import {AxiosResponse} from 'axios';
import {IClientSelected} from '../components/bulk-user-upload-module/BulkUserUploadScreen';
import {
  CompletedMappedDataExportRequest,
  CompletedMappedDataListRequest,
  CompletedMappedDataProjectTotalsQuery,
  CompletedMappedDataTotalsRequest,
} from '../types/completed-mapped-data/completedMappedDataType';
import {UploadPayload} from '../types/concierge-reports/conciergeReportsType';
import {GetIntakeAuditReportReq} from '../types/types';
import {IFiltersData} from '../utils/inTakeAuditReportUtils';
import apiClient from './index';
import { COUNTRY_CODES } from '../constants/constants';

const version = 'v1';
const reportingManagementEndpoint = (path: string) =>
  `${version}/reportingmanagement${path}`;

const clientReportManagementEndpoint = (path: string) =>
  `/${version}/clientmanagement${path}`;

const INTAKE_AUDIT = 'intake-audit';
export const INTAKE_AUDIT_EXPORT_XLSX_URL = `${INTAKE_AUDIT}/export/xlsx`;

const HIGH_LEVEL_PRODUCTION_METRICS = 'high-level-production-metrics';
export const HIGH_LEVEL_PRODUCTION_METRICS_URL = `/${HIGH_LEVEL_PRODUCTION_METRICS}/export/csv`;

// to fetch list of workspaces by workspaceId, clientId and projectId
export const getWorkspaceDatasetByWorkspaceId = (
  workspaceId: string | null,
  params?: string
) => {
  return apiClient.get(
    reportingManagementEndpoint(`/workspace/${workspaceId}/dataset${params}`)
  );
};
//to fetch list of workspaces by clientId
export const getWorkspaceSummary = (params?: string) => {
  return apiClient.get(
    reportingManagementEndpoint(`/report-summary?` + params)
  );
};

//for getting reportpage Loaded and Released
export const getHostingVolumeReportById = (
  clientId: string | null | undefined,
  projectId: string | null | undefined,
  workspaceId: string | null | undefined
) => {
  return apiClient.get(
    reportingManagementEndpoint(
      `/hosting-volume/summary?clientID=${clientId}&projectID=${projectId}&workspaceID=${workspaceId}`
    )
  );
};

export const getEvidenceSourcesByWorkspaceId = (
  workspaceId: string | null,
  params?: string
) => {
  return apiClient.get(
    reportingManagementEndpoint(
      `/workspaces/${workspaceId}/evidencesources?${params}`
    )
  );
};

// For inprogress evidence
export const getInprogressEvidenceSourcesByWorkspaceId = (
  workspaceId: string | null,
  params?: string
) => {
  return apiClient.get(
    reportingManagementEndpoint(
      `/workspaces/${workspaceId}/inprogressevidencesources?${params}`
    )
  );
};

/**
 * Upload concierge report file
 * @param file
 * @returns
 */
export const uploadConciergeReportsFile = (payloadObj: UploadPayload) => {
  const formData = new FormData();

  Object.keys(payloadObj).forEach((key) => {
    const value = payloadObj[key];

    // Check if the key is "userIdList" and handle it as an array
    if (Array.isArray(value)) {
      value.forEach((userId) => {
        formData.append(`${key}`, userId); // Append each userId with an index
      });
    } else {
      // For all other properties, append them directly
      formData.append(key, value);
    }
  });

  return apiClient.post(
    reportingManagementEndpoint(`/conciergereports`),
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
};

//Api to fecth list of concierge reports
export const fetchConciergeReportsList = async (params?: string) => {
  const reportList = await apiClient.get(
    reportingManagementEndpoint(`/conciergereports` + params)
  );
  return reportList.data;
};
//Api to fecth list of table data for high-level-production-matric reports
export const fecthHighlevelIntakeTableData = async (params?: string) => {
  try {
    const reportList = await apiClient.get(
      reportingManagementEndpoint(`/high-level-production-metrics` + params)
    );
    return reportList.data;
  } catch (error: unknown) {
    const e = error as string;
    throw new Error(e);
  }
};
//Api to fecth list of praductionData high-level-production-matric reports
export const fecthProductionNameData = async (params?: string) => {
  try {
    const reportList = await apiClient.get(
      reportingManagementEndpoint(
        `/high-level-production-metrics/filter/productionnames` + params
      )
    );
    return reportList.data;
  } catch (error: unknown) {
    const e = error as string;
    throw new Error(e);
  }
};

//Api to delete the selected concierge reports
export const deleteConciergeReportsFile = (reportIds: (string | number)[]) => {
  //adding locally to avoid the jwt decode error as initially token is undefined
  //to get the logged in userid to pass in headers
  return apiClient.delete(reportingManagementEndpoint(`/conciergereports`), {
    data: reportIds,
  });
};

/**
 * Upload concierge report file
 * @param file
 * @returns
 */
export const conciergeReportFileExists = (fileName: object) => {
  return apiClient.post(
    reportingManagementEndpoint(`/conciergereports/validate-duplicate-file`),
    fileName
  );
};

/**
 * Download concierge report file
 * @param param
 * @returns
 */
export function downloadConciergeReportFile(param: string) {
  return apiClient.get(
    reportingManagementEndpoint(`/conciergereports/${param}`),
    {
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream',
      },
    }
  );
}

// To fetch Processing summary report of dataset by datasetId, clientId, workspaceId and projectId
export const getProcessingSummaryByDataSetID = (
  dataSetId: string,
  params?: string,
  signal?: AbortSignal
) => {
  return apiClient.get(
    reportingManagementEndpoint(`/${dataSetId}/processing-summary${params}`),
    {
      signal: signal,
    }
  );
};

// To Fetch Processing Exception Report Count by datasetId, clientId, workspaceId and projectId
export const getProcessingExceptionReportCount = (
  clientId: string,
  projectId: string,
  workspaceId: string,
  datasets: string,
  signal?: AbortSignal
) => {
  const params = {
    clientId,
    projectId,
    workspaceId,
    datasets,
  };
  return apiClient.get(
    reportingManagementEndpoint('/processing-exceptions/counts'),
    {params, signal: signal}
  );
};

// To Fetch Processing Exception Report details by datasetId, clientId, workspaceId, projectId and category
export const getProcessingExceptionReportDetails = (
  pageSize: number,
  pageNumber: number,
  clientId?: string | null,
  projectId?: string | null,
  workspaceId?: string | null,
  datasets?: string | null,
  exceptionCategory?: string,
  signal?: AbortSignal
) => {
  const params = {
    clientId,
    projectId,
    workspaceId,
    datasets,
    exceptionCategory,
    pageSize,
    pageNumber,
  };
  return apiClient.get(reportingManagementEndpoint(`/processing-exceptions`), {
    params,
    signal: signal,
  });
};

export const getIntakeAuditReport = (
  params: string,
  reuestBody: GetIntakeAuditReportReq
) => {
  return apiClient.post(
    reportingManagementEndpoint(`/${INTAKE_AUDIT}${params}`),
    reuestBody
  );
};

export const getProcessingInProgressReport = (params?: string) => {
  return apiClient.get(
    reportingManagementEndpoint(`/processing-in-progress?${params}`)
  );
};

export const getDatasetProcessingProgressReport = (params?: string) => {
  return apiClient.get(
    reportingManagementEndpoint(`/processing-in-progress/refresh?${params}`)
  );
};

export const downloadDataSetProcessingProgressCSV = (params?: string) => {
  return apiClient.get(
    reportingManagementEndpoint(`/processing-in-progress/export/csv?${params}`)
  );
};

export const downloadAsXLSX = (
  requestURL: string,
  params: string,
  requestBody: GetIntakeAuditReportReq
) => {
  return apiClient.post(
    reportingManagementEndpoint(`/${requestURL}${params}`),
    requestBody,
    {
      responseType: 'blob',
    }
  );
};

export const getIntakeAudit = (params: string) => {
  return apiClient.post(
    reportingManagementEndpoint(`/${INTAKE_AUDIT}${params}`)
  );
};

export const getSavedFilters = (params: string) => {
  return apiClient.get(reportingManagementEndpoint(`/filters${params}`));
};

export const getClientOverView = (params: string) => {
  return apiClient.get(
    reportingManagementEndpoint(`/clients/${params}/overview`)
  );
};

export const clearFilters = (reportId: number, params: string) => {
  return apiClient.delete(
    reportingManagementEndpoint(`/filters/${reportId}${params}`)
  );
};

export const saveFilters = (
  reportId: number,
  params: string,
  requestBody?: IFiltersData | Record<string, unknown>
) => {
  return apiClient.put(
    reportingManagementEndpoint(`/filters/${reportId}${params}`),
    requestBody
  );
};

export const getFilterCustodians = (paramString: string) => {
  return apiClient.get(
    reportingManagementEndpoint(`/intake-audit/filter/custodians${paramString}`)
  );
};

export const getFilterItemCategories = (paramString: string) => {
  return apiClient.get(
    reportingManagementEndpoint(
      `/intake-audit/filter/item-categories${paramString}`
    )
  );
};

export const getInvoiceList = (params?: string) => {
  return apiClient.get(reportingManagementEndpoint('/invoices' + params));
};

export const getInvoiceDownload = (invoiceId: string, params?: string) => {
  return apiClient.get(
    reportingManagementEndpoint('/invoices/' + invoiceId + '/download' + params)
  );
};

export const getUsernamesList = (
  clientId: string,
  projectId: string,
  workspaceId: string
) => {
  const endpoint = reportingManagementEndpoint(
    '/hosting-user-summary/filter/usernames'
  );
  const params = {
    clientId,
    projectId,
    workspaceId,
  };

  return apiClient.get(endpoint, {params});
};

export const getLastAccessedDateList = (
  clientId: string,
  projectId: string,
  workspaceId: string
) => {
  const endpoint = reportingManagementEndpoint(
    '/hosting-user-summary/filter/lastaccessed'
  );
  const params = {
    clientId,
    projectId,
    workspaceId,
  };

  return apiClient.get(endpoint, {params});
};

export const getHostingUserSummary = (
  clientId: string,
  projectId: string,
  workspaceId: string,
  userName: string,
  lastAccessedDate: string,
  PageNumber: number,
  PageSize: number
) => {
  const endpoint = reportingManagementEndpoint('/hosting-user-summary');

  const params = {
    clientId,
    projectId,
    workspaceId,
    userName,
    lastAccessedDate,
    PageSize,
    PageNumber,
  };

  return apiClient.get(endpoint, {params});
};

export const getClientsList = (params?: string) => {
  let url = '/clients/conciergeupload';
  if (params) {
    url = `${url}${params}`;
  }
  return apiClient.get(clientReportManagementEndpoint(url));
};

export const getClientsAsLookup = async (params?: string) => {
  const queryString = `?searchTerm=${params ? params : ''}&pageNumber=1&pageSize=12`;
  const clients = await getClientsList(queryString);
  const response = {
    data: clients?.data?.data?.map((client: IClientSelected) => {
      return {
        id: client.clientId,
        name: client.name,
        projects: client?.projects,
      };
    }),
  };
  return response;
};

export const getProjectsByClientId = (
  clientId: string | number,
  searchTerm: string
) => {
  const queryString = `?searchTerm=${searchTerm ? searchTerm : ''}&pageNumber=1&pageSize=12`;
  return apiClient.post(
    clientReportManagementEndpoint(
      `/clients/${clientId}/projects/me${queryString}`
    ),
    {}
  );
};

export const getExportReportXlsx = (
  clientId: string,
  projectId: string,
  workspaceId: string,
  reportURL: string,
  datasets?: string | null,
  exceptionCategory?: string | null,
  pageCount?: number,
  pageSize?: number
): Promise<AxiosResponse<Blob>> => {
  const endpoint = reportingManagementEndpoint(`${reportURL}/export/xlsx`);
  const params = {
    clientId,
    projectId,
    workspaceId,
    datasets,
    exceptionCategory,
    pageCount,
    pageSize,
  };

  return apiClient.get<Blob>(endpoint, {
    params,
    responseType: 'blob',
    headers: {
      Accept:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
  });
};

export const downloadHighLevelProductionsReportXlsx = (
  clientId: string,
  projectId: string,
  workspaceId: string
): Promise<AxiosResponse<Blob>> => {
  const endpoint = reportingManagementEndpoint(
    '/high-level-production-metrics/export/xlsx'
  );
  const params = {
    clientId,
    projectId,
    workspaceId,
  };

  return apiClient.get<Blob>(endpoint, {params});
};

export const fetchHostingVolumeDatasetSummary = (
  clientId?: string | null,
  projectId?: string | null,
  workspaceId?: string | null
) => {
  const params = {
    clientId,
    projectId,
    workspaceId,
  };
  return apiClient.get(
    reportingManagementEndpoint('/hosting-volume/dataset-summary'),
    {params}
  );
};

export const fetchHostingVolumeSummaryDetails = (
  clientId?: string | null,
  projectId?: string | null,
  workspaceId?: string | null
) => {
  const params = {
    clientId,
    projectId,
    workspaceId,
  };
  return apiClient.get(reportingManagementEndpoint('/hosting-volume/summary'), {
    params,
  });
};

export const fetchHostingVolumeReportDetails = (
  clientId?: string | null,
  projectId?: string | null,
  workspaceId?: string | null,
  pageNumber?: number,
  pageSize?: number
) => {
  const params = {
    clientId,
    projectId,
    workspaceId,
    pageNumber,
    pageSize,
  };
  return apiClient.get(reportingManagementEndpoint('/hosting-volume'), {
    params,
  });
};

export const getRefreshDates = () => {
  return apiClient.get(reportingManagementEndpoint(`/refresh-dates`));
};

export const getDataSetProcessed = (params: string) => {
  return apiClient.get(
    reportingManagementEndpoint(`/datasetsprocessed?${params}`)
  );
};

export const getProductionsCompleted = (params: string) => {
  return apiClient.get(
    reportingManagementEndpoint(`/productioncompleted?${params}`)
  );
};

export const fetchTotalDataUnderManagement = (projectAlphaCode: string) => {
  const params = {
    projectAlphaCode,
  };

  return apiClient.get(
    reportingManagementEndpoint('/total-data-under-management'),
    {
      params,
    }
  );
};

export const fetchCompletedMappedDataTotals = (
  requestBody: CompletedMappedDataTotalsRequest
) => {
  return apiClient.post(
    reportingManagementEndpoint(`/completed-mapped-data-totals`),
    requestBody
  );
};

export const fetchCompletedMappedDataList = (
  requestBody: CompletedMappedDataListRequest
) => {
  return apiClient.post(
    reportingManagementEndpoint(`/completed-mapped-data`),
    requestBody
  );
};

export const exportCompletedMappedData = (
  requestBody: CompletedMappedDataExportRequest
) => {
  return apiClient.post(
    reportingManagementEndpoint(`/completed-mapped-data/export/xlsx`),
    requestBody,
    {
      responseType: 'blob',
    }
  );
};

export const getCompletedMappedDataProjectTotals = (
  requestBody: CompletedMappedDataProjectTotalsQuery
) => {
  return apiClient.post(
    reportingManagementEndpoint(`/completed-mapped-data-project-totals`),
    requestBody
  );
};

export const getPowerBIProjects = (clientId: string, queryParam: string) => {
  return apiClient.get(
    clientReportManagementEndpoint(
      `/clients/${clientId}/projects/pbi-report/lookup?${queryParam}`
    )
  );
};

export const getPowerBiReportGroups = (
  clientId: string,
  projectId: string,
  groupCode?: string,
  countryCode?: string
) => {
  const groupCodeParam = groupCode ? `&groupCode=${groupCode}` : '';
  const countryCodeParam = countryCode ? `&countryCode=${countryCode}` : COUNTRY_CODES.UNITED_STATE;

  return apiClient.get(
    reportingManagementEndpoint(
      `/pbi-report-groups?clientId=${clientId}&projectId=${projectId}${groupCodeParam}${countryCodeParam}`
    )
  );
};

export const getEmbedReportsConfig = (
  reportId: string,
  clientId: string,
  projectId: string,
  countryCode?: string
) => {
  const countryCodeParam = countryCode ? `&countryCode=${countryCode}` : '';

  return apiClient.get(
    reportingManagementEndpoint(
      `/pbi-reports/${reportId}?clientId=${clientId}&projectId=${projectId}${countryCodeParam}`
    )
  );
};

export const exportPowerBiReport = (
  reportId: string,
  clientId: string,
  projectId: string,
  format: 'PDF' | 'XLSX',
  pageName?: string
) => {
  return apiClient.post(
    reportingManagementEndpoint(
      `/pbi-reports/${reportId}/export?clientId=${clientId}&projectId=${projectId}`
    ),
    {format, pageName},
    {
      responseType: 'blob',
    }
  );
};
